/**
 * A lógica da tela de laboratório — o que separa um formulário de um roteiro.
 *
 * A rota `/v1/laboratorio/bmp/prever` aceita vinte análises, todas opcionais, e
 * devolve seis modelos dizendo quais rodaram. Transformar isso num formulário
 * de vinte campos em branco seria a tradução literal e a pior tela possível:
 * quem chega aqui não tem vinte análises, tem UM laudo na mão e uma pergunta
 * ("vale a pena pagar mais alguma análise?").
 *
 * Então a tela responde essa pergunta, e é aqui que ela é calculada.
 *
 * **A sutileza que decide se o número é honesto.** Um modelo só passa a rodar
 * quando TODAS as análises que ele exige existem. Contar "em quantos modelos a
 * lignina aparece" dá 6 e é uma promessa falsa: medir lignina sozinha põe 3
 * para rodar, não 6. A conta que vale é `faltando.length === 1` — quantos
 * modelos ficam a UMA medição de distância. O número maior é o que agrada; por
 * isso o padrão aqui é o menor, e o maior só aparece ao lado dele, nomeado
 * como outra coisa.
 */

/**
 * Nome de campo → como o laboratório chama a análise, e em que laudo ela vem.
 *
 * O `grupo` não é arrumação: é o que decide se o consultor consegue responder.
 * Ele não tem "ácidos urônicos" solto na cabeça — ele tem um laudo de fibras e
 * um de bromatologia, e precisa saber de qual papel copiar cada número.
 */
export const ANALISES = {
  st_pct: { rotulo: 'Sólidos totais', unidade: '% da massa úmida', grupo: 'imediata' },
  cinzas_pct_st: { rotulo: 'Cinzas', unidade: '% do ST', grupo: 'imediata' },

  lignina_pct_st: { rotulo: 'Lignina', unidade: '% do ST', grupo: 'fibras' },
  celulose_pct_st: { rotulo: 'Celulose', unidade: '% do ST', grupo: 'fibras' },
  hemicelulose_pct_st: { rotulo: 'Hemicelulose', unidade: '% do ST', grupo: 'fibras' },
  adf_pct_st: { rotulo: 'Fibra em detergente ácido (FDA)', unidade: '% do ST', grupo: 'fibras' },
  ndf_pct_st: { rotulo: 'Fibra em detergente neutro (FDN)', unidade: '% do ST', grupo: 'fibras' },

  proteina_pct_st: { rotulo: 'Proteína bruta', unidade: '% do ST', grupo: 'bromatologica' },
  lipidio_pct_st: { rotulo: 'Lipídios (extrato etéreo)', unidade: '% do ST', grupo: 'bromatologica' },

  celulose_cristalina_pct_st: {
    rotulo: 'Celulose cristalina', unidade: '% do ST', grupo: 'fina',
  },
  holocelulose_amorfa_pct_st: {
    rotulo: 'Holocelulose amorfa', unidade: '% do ST', grupo: 'fina',
  },
  acucares_soluveis_pct_st: {
    rotulo: 'Açúcares solúveis', unidade: '% do ST', grupo: 'fina',
  },
  acidos_uronicos_pct_st: {
    rotulo: 'Ácidos urônicos', unidade: '% do ST', grupo: 'fina',
  },
  carboidratos_soluveis_pct_st: {
    rotulo: 'Carboidratos solúveis', unidade: '% do ST', grupo: 'fina',
  },

  dqo_g_l: { rotulo: 'DQO', unidade: 'g/L', grupo: 'quimica' },

  // Nao e uma analise: e um laudo inteiro (C, H, N, O, S). Aparece aqui
  // porque o motor a publica em `faltando` como qualquer outro campo, e uma
  // tela que a omitisse mandaria medir lignina para destravar um modelo que
  // tambem espera o CHNOS.
  elementar: { rotulo: 'Análise elementar (CHNOS)', unidade: '% do SV', grupo: 'quimica' },
};

/** Os laudos, na ordem em que um laboratório os entrega — do comum ao raro. */
export const GRUPOS = [
  {
    chave: 'imediata',
    rotulo: 'Análise imediata',
    explicacao: 'Sai em qualquer laboratório. É o que converte um modelo de base ST para base SV.',
  },
  {
    chave: 'fibras',
    rotulo: 'Fibras (Van Soest)',
    explicacao: 'O laudo que mais move o resultado: a lignina sozinha põe três modelos para rodar.',
  },
  {
    chave: 'bromatologica',
    rotulo: 'Bromatológica',
    explicacao: 'Proteína e gordura. Costuma vir junto com a de fibras, em laboratório de nutrição animal.',
  },
  {
    chave: 'fina',
    rotulo: 'Fracionamento fino',
    explicacao:
      'Raro e caro, pedido quase só em pesquisa. Só um modelo do comitê depende dele — ' +
      'não vale a corrida se o objetivo é dimensionar um biodigestor.',
  },
  {
    chave: 'quimica',
    rotulo: 'Química',
    explicacao: 'DQO em base líquida, para material bombeável.',
  },
];

/** Nome legível de uma análise, mesmo se o motor inventar um campo novo amanhã. */
export function nomeDaAnalise(campo) {
  return ANALISES[campo]?.rotulo ?? campo;
}

/**
 * O roteiro: o que medir primeiro, e quantos modelos cada medição libera.
 *
 * Devolve a lista ordenada por `desbloqueia` (modelos que passam a rodar com
 * ESTA análise e nada mais), com `participa` ao lado (em quantos modelos ela
 * aparece, incluindo os que vão continuar precisando de outras). Os dois
 * números juntos são a única forma honesta de responder "vale a pena medir?".
 *
 * Análise que não desbloqueia nada sozinha continua na lista, no fim, porque
 * ela pode ser a segunda metade de um par — e esconder isso faria a tela
 * parecer dizer que medi-la é inútil.
 */
export function oQueMedirPrimeiro(modelos) {
  const pendentes = (modelos ?? []).filter((m) => !m.aplicado && (m.faltando?.length ?? 0) > 0);
  const por = new Map();

  for (const m of pendentes) {
    const faltando = m.faltando ?? [];
    for (const campo of faltando) {
      if (!por.has(campo)) {
        por.set(campo, { campo, rotulo: nomeDaAnalise(campo), desbloqueia: [], participa: [] });
      }
      const entrada = por.get(campo);
      entrada.participa.push(m.chave);
      if (faltando.length === 1) entrada.desbloqueia.push(m.chave);
    }
  }

  return [...por.values()].sort(
    (a, b) =>
      b.desbloqueia.length - a.desbloqueia.length ||
      b.participa.length - a.participa.length ||
      a.rotulo.localeCompare(b.rotulo, 'pt-BR'),
  );
}

/**
 * A reprodutibilidade do próprio ensaio BMP entre laboratórios, em %.
 *
 * Não é detalhe de rodapé: é o piso contra o qual qualquer concordância de
 * modelos tem de ser lida. Modelos que concordam dentro de 20% não estão
 * concordando melhor que dois laboratórios medindo o MESMO material — estão
 * concordando dentro do ruído, e anunciar isso como precisão é a forma mais
 * fácil de a tela mentir com números certos.
 */
export const RUIDO_DO_ENSAIO_PCT = [21, 33];

/**
 * Como ler a discordância entre os modelos.
 *
 * O corte de 30% é o que a própria API usa para dizer que nenhuma média é
 * defensável. Abaixo dele existe um segundo corte, menos óbvio e mais
 * importante: concordância melhor que o ruído do ensaio não é concordância
 * boa, é concordância que não significa nada.
 */
export function lerDispersao(pct) {
  if (pct === null || pct === undefined) return null;
  if (pct > 30) {
    return {
      nivel: 'ruim',
      rotulo: 'os modelos não concordam',
      porque:
        'Acima de 30% de discordância nenhuma média é defensável. A discordância ' +
        'é o resultado: ela diz que a composição informada não determina o BMP ' +
        'deste material.',
    };
  }
  if (pct >= RUIDO_DO_ENSAIO_PCT[0]) {
    return {
      nivel: 'atencao',
      rotulo: 'concordam dentro do ruído do próprio ensaio',
      porque:
        `Os modelos ficaram em ${pct.toFixed(0)}%, dentro da faixa de ` +
        `${RUIDO_DO_ENSAIO_PCT[0]} a ${RUIDO_DO_ENSAIO_PCT[1]}% em que dois ` +
        'laboratórios discordam medindo o mesmo material. Concordar aqui não é ' +
        'acertar — é empatar com a incerteza do ensaio.',
    };
  }
  return {
    nivel: 'bom',
    rotulo: 'os modelos convergem',
    porque:
      `${pct.toFixed(0)}% de discordância é menos que os ` +
      `${RUIDO_DO_ENSAIO_PCT[0]} a ${RUIDO_DO_ENSAIO_PCT[1]}% de dispersão entre ` +
      'laboratórios do próprio ensaio BMP. Isso limita o que se pode prometer: ' +
      'a previsão não pode ser mais exata que a medição que a validaria.',
  };
}

/**
 * A escala de oxidação do carbono, de +4 (CO₂) a −4 (CH₄).
 *
 * Posição em 0–1 para desenhar a régua. Sem `clamp` a barra de um laudo com
 * digitação errada sairia da caixa; com `clamp` ela encosta no fim e o aviso
 * da API explica. Encostar é informação, sumir não é.
 */
export function posicaoNaEscalaEmoc(emoc) {
  const bruto = (4 - emoc) / 8;
  return Math.min(1, Math.max(0, bruto));
}

/**
 * Quantos dos critérios informados passaram, para o resumo da conferência.
 *
 * `nao_conferido` entra no resultado de propósito: um ensaio sem avisos porque
 * nada foi informado não é um ensaio aprovado, e um placar que só conte os
 * avisos diria exatamente isso.
 */
export function placarDoEnsaio(resposta) {
  if (!resposta) return null;
  const conferidos = resposta.conferido?.length ?? 0;
  const faltando = resposta.nao_conferido?.length ?? 0;
  const graves = (resposta.avisos ?? []).filter((a) => a.grave).length;
  const leves = (resposta.avisos ?? []).length - graves;
  return {
    conferidos,
    faltando,
    graves,
    leves,
    vazio: conferidos === 0,
    completo: faltando === 0,
  };
}
